import React, { useState, useEffect, useRef, useMemo } from 'react';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, getFileUrl } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory } from 'lucide-react';
import { HoldRepeatButton } from './HoldRepeatButton';
import { InvoiceView } from '../InvoiceView';
import { useConfirm } from '@/components/ConfirmProvider';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmOrderDialog } from './ordersTab/ConfirmOrderDialog';
import { DispatchOrderDialog } from './ordersTab/DispatchOrderDialog';
import { DeleteOrderDialog, PostDispatchNotifyDialog } from './ordersTab/SimpleDialogs';
import { ReviewOrderDialog } from './ordersTab/ReviewOrderDialog';
import { CrmDealerDialog } from './ordersTab/CrmDealerDialog';
import { OutstandingDetailDialog } from './ordersTab/OutstandingDetailDialog';
import { FreightDetailDialog } from './ordersTab/FreightDetailDialog';
import { EditOrderDialog } from './ordersTab/EditOrderDialog';
import { BulkRescheduleDialog, BulkConfirmDialog } from './ordersTab/BulkDialogs';

// Memoized single order row. Extracted + React.memo'd so that a change to one
// order (edit / hold / confirm / manual amount / description) — or typing in
// one row's description/seq input — only re-renders THAT row, instead of the
// whole (up to hundreds of rows) list. All parent handlers are funnelled
// through a single STABLE `api` bag (identity never changes) so memo isn't
// defeated by fresh function identities on every parent render. Per-row
// mutable data is passed as primitives (isSelected, drafts, isJustCreated,
// outstanding) so React.memo's shallow prop compare correctly skips unchanged
// rows.
const OrderRow = React.memo(function OrderRow({
  order, idx, isSelected, isJustCreated, manualDraft, descDraft, town,
  outstanding, freight, priceOverridden, productDiscountByPid, userRole, isStrictAdmin, api,
}) {
  const getTotalQty = (items) => items.reduce((s, i) => s + i.quantity, 0);
  return (
    <tr key={order.id} style={{ borderBottom: '1px solid #E2DFD8', background: idx % 2 === 0 ? '#FFFFFF' : '#FBF9F6' }} className={`hover:bg-[#F5F1E8] transition-colors ${isJustCreated ? 'row-just-created' : ''}`} data-testid="admin-order-row">
      <td className="py-1.5 px-2 text-center" style={{ width: '32px' }}>
        <input
          type="checkbox"
          checked={isSelected}
          disabled={order.status === 'delivered' || order.status === 'dispatched' || order.status === 'partial_dispatched'}
          onChange={() => api.toggleSelectOne(order.id)}
          className="cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
          data-testid="bulk-select-row"
          title={
            order.status === 'delivered' ? 'Delivered orders cannot be selected'
            : (order.status === 'dispatched' || order.status === 'partial_dispatched') ? 'Dispatched orders cannot be rescheduled / confirmed'
            : 'Select for bulk action'
          }
        />
      </td>
      <td className="py-1.5 px-2 text-sm align-top" style={{ width: '90px', minWidth: '90px' }}>
        <div className="flex items-center gap-1">
          <div className="text-center" style={{ minWidth: '22px' }}>{idx + 1}</div>
          <input
            type="number"
            value={manualDraft !== undefined ? manualDraft : (order.manual_amount ?? '')}
            onChange={(e) => api.setManualDraft(order.id, e.target.value)}
            onBlur={(e) => api.saveManualAmount(order.id, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const inputs = Array.from(document.querySelectorAll('[data-testid="seq-input"]'));
                const currentIdx = inputs.indexOf(e.target);
                const next = inputs[currentIdx + 1];
                if (next) { next.focus(); next.select(); } else { e.target.blur(); }
              }
            }}
            className="h-7 w-12 text-center text-sm font-bold rounded p-0 focus:outline-none focus:ring-1 focus:ring-[#2B5339]"
            style={{ color: '#2B5339', border: '1px solid #E2DFD8', background: '#FFFFFF' }}
            placeholder="—"
            data-testid="seq-input"
          />
        </div>
      </td>
      <td className="py-1.5 px-2 align-top" style={order.on_hold ? { minWidth: '220px', background: 'rgba(192, 92, 53, 0.3)' } : { minWidth: '220px' }}>
        <button
          type="button"
          onClick={() => api.openCrm(order)}
          className="text-left hover:underline focus:outline-none w-full"
          style={{ color: '#2B5339' }}
          data-testid="dealer-name-link"
          title="Click to view this dealer's CRM details (outstanding invoices + dispatches)"
        >
          <div className="font-medium text-sm">
            {order.dealer_name}
            {order.on_hold ? ' · HOLD' : ''}
            {priceOverridden && (
              <span
                className="ml-2 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded align-middle"
                style={{ background: '#C05C35', color: 'white', letterSpacing: '0.03em' }}
                title="One or more items have a manual rate (virtual product or master-price override)"
                data-testid="price-overridden-badge"
              >
                ₹ OVR
              </span>
            )}
          </div>
          <div className="text-xs" style={{ color: '#5C6B61' }}>{town}</div>
        </button>
      </td>
      <td className="py-1.5 px-2 align-top" style={{ minWidth: '220px' }} data-testid="product-list-cell">
        <div
          role="button"
          tabIndex={0}
          onClick={() => api.setReviewOrder(order)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); api.setReviewOrder(order); } }}
          className="text-xs leading-tight cursor-pointer hover:underline whitespace-normal"
          style={{ color: '#5C6B61' }}
          title="Click to review order"
          data-testid="product-list-inline"
        >
          {order.items.map((item, i) => (
            <div key={i}>{i + 1}. {item.product_name} x{item.quantity}</div>
          ))}
        </div>
        <div className="mt-1.5 text-[11px] font-bold" style={{ color: '#2B5339' }} data-testid="seq-total-qty" title="Total quantity">
          Bags : {getTotalQty(order.items)}
        </div>
      </td>
      <td className="py-1.5 px-2 text-xs align-top whitespace-nowrap" style={{ minWidth: '140px' }}>
        <div className="text-center">
          {order.vehicle_number ? (() => {
            const latestDispatch = (order.dispatches || []).reduce((m, d) => {
              const t = d?.dispatched_at || '';
              return t > m ? t : m;
            }, '');
            const dispAt = latestDispatch || order.updated_at;
            return (
              <div style={{ color: '#C05C35', lineHeight: '1.15' }}>
                <div className="font-medium">{api.fmtDateOnly(dispAt)}</div>
                <div className="text-[10px]" style={{ color: '#C05C35' }}>{api.fmtTimeOnly(dispAt)}</div>
              </div>
            );
          })() : order.delivery_date ? (
            <div style={{ color: '#2B5339', lineHeight: '1.15' }}>
              <div className="font-medium">{api.fmtDate(order.delivery_date)}</div>
              <div className="text-[10px]" style={{ color: '#2B5339' }}>{order.delivery_time || '\u00A0'}</div>
            </div>
          ) : (
            <span style={{ color: '#E2DFD8' }}>-</span>
          )}
        </div>
      </td>
      <td className="py-1.5 px-2 text-sm align-top" style={{ minWidth: '220px' }}>
        <div className="relative" data-testid="product-desc-box">
          {(() => {
            const val = descDraft !== undefined ? descDraft : (order.manual_description ?? '');
            const computedRows = Math.max(1, Math.min(10, (val.match(/\n/g) || []).length + 1 + Math.floor(val.length / 32)));
            const hasVal = val && val.length > 0;
            return (
              <>
                <Textarea
                  value={val}
                  onChange={(e) => api.setDescDraft(order.id, e.target.value)}
                  onBlur={(e) => {
                    const raw = e.target.value;
                    if (raw !== (order.manual_description ?? '')) api.saveManualDescription(order.id, raw);
                  }}
                  placeholder="Add description..."
                  rows={computedRows}
                  className="w-full text-xs resize-y py-1 px-2 pr-6 font-medium"
                  style={{ color: '#C05C35' }}
                  data-testid="manual-description-input"
                />
                {hasVal && (
                  <button
                    type="button"
                    onClick={() => {
                      api.setDescDraft(order.id, '');
                      api.saveManualDescription(order.id, '');
                    }}
                    className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold hover:bg-[#F3E0D9] leading-none"
                    style={{ color: '#C05C35', background: '#FBF9F6', border: '1px solid #E2DFD8' }}
                    title="Clear description"
                    aria-label="Clear description"
                    data-testid="clear-description-btn"
                  >
                    ×
                  </button>
                )}
              </>
            );
          })()}
        </div>
      </td>
      <td className="py-1.5 px-3 text-sm text-left font-medium whitespace-nowrap" data-testid="order-bill-outstanding">
        <div data-testid="order-bill-amount">
          {(() => {
            let net = 0;
            let hasDisc = false;
            (order.items || []).forEach(it => {
              const pct = it.discount_percent != null ? Number(it.discount_percent) : (productDiscountByPid[it.product_id] ?? (order.discount_percent == null ? 10 : order.discount_percent));
              if (pct > 0) hasDisc = true;
              net += Number(it.total || 0) * (1 - pct / 100);
            });
            return (
              <>
                <span>{'\u20B9'}{net.toFixed(2)}</span>
                {hasDisc && (
                  <span
                    className="ml-1 inline-block text-[9px] font-semibold px-1 py-0.5 rounded align-middle"
                    style={{ background: '#EFF4EE', color: '#2B5339' }}
                    title={`Gross: \u20B9${Number(order.total_amount || 0).toFixed(2)} — discount applied per-product`}
                  >
                    after disc
                  </span>
                )}
              </>
            );
          })()}
        </div>
        <div className="mt-0.5 text-xs" data-testid="order-outstanding-amount">
          {(() => {
            if (!outstanding || !outstanding.outstanding_total) {
              return <span style={{ color: '#B9BEB6' }}>—</span>;
            }
            const amt = Number(outstanding.outstanding_total).toLocaleString('en-IN', { maximumFractionDigits: 0 });
            const overdue = outstanding.is_overdue || (outstanding.oldest_od_days || 0) > 0;
            return (
              <button
                type="button"
                onClick={() => api.openOutstanding(order)}
                className="hover:underline focus:outline-none"
                style={{ color: '#C05C35', fontWeight: overdue ? 600 : 500 }}
                title="Outstanding from CRM — click to view invoice breakdown"
                data-testid="outstanding-amount-link"
              >
                {'\u20B9'}{amt}
              </button>
            );
          })()}
        </div>
        {freight && (
          <div className="mt-0.5 text-xs" data-testid="order-freight-amount">
            <button
              type="button"
              onClick={() => api.openFreight(order)}
              className="hover:underline focus:outline-none inline-flex items-center gap-1"
              style={{ color: '#2B8A4B', fontWeight: 600 }}
              title={freight.is_estimate ? 'Estimated freight payable by dealer (pre-dispatch) — click for details' : 'Freight payable by dealer — click to view freight details'}
              data-testid="freight-amount-link"
            >
              <Truck className="w-3 h-3" />
              {freight.is_estimate ? '~' : ''}{'\u20B9'}{Number(freight.by_dealer_total || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </button>
          </div>
        )}
      </td>
      <td className="py-1.5 px-3 align-top" style={{ minWidth: '240px' }}>
        <div className="text-left mb-1">{api.getStatusBadge(order.status)}</div>
        <div className="flex flex-nowrap gap-1 justify-start items-center flex-wrap">
          {(order.status === 'confirmed' || order.status === 'partial_dispatched') && !order.on_hold && api.canDispatchOrder(order) && (
            <Button size="sm" className="text-xs px-2 py-1 h-7" style={{ background: '#C05C35', fontSize: '11px' }} onClick={() => api.openDispatch(order)} data-testid="dispatch-order-btn">
              {order.status === 'partial_dispatched' ? 'Dispatch rest' : 'Dispatch'}
            </Button>
          )}
          {(order.status === 'confirmed' || order.status === 'partial_dispatched') && !order.on_hold && !api.canDispatchOrder(order) && (
            <span className="text-[10px] px-1.5 py-1" style={{ color: '#5C6B61' }} title={`Dispatch unlocks on schedule date (${order.delivery_date || '—'})`}>
              Not today
            </span>
          )}
          {(order.status === 'confirmed' || order.status === 'partial_dispatched') && order.on_hold && (
            <span className="text-xs px-2 py-1" style={{ color: '#C05C35' }} title="Release hold to enable dispatch">
              Held
            </span>
          )}
          {api.can('orders', 'revert') && api.getRevertOptions(order.status).length > 0 && (
            <select
              className="text-xs border rounded px-1 py-1 h-7"
              style={{ borderColor: '#E2DFD8', color: '#C05C35', fontSize: '11px' }}
              defaultValue=""
              onChange={(e) => { if (e.target.value) { api.handleRevert(order, e.target.value); e.target.value = ''; } }}
              data-testid="revert-order-select"
            >
              <option value="" disabled>Revert</option>
              {api.getRevertOptions(order.status).map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          )}
          {(order.status === 'pending' || order.status === 'confirmed') && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs px-2 py-1 h-7"
              style={order.on_hold ? { fontSize: '11px', background: '#C05C35', color: 'white', borderColor: '#C05C35' } : { fontSize: '11px', color: '#C05C35', borderColor: '#C05C35' }}
              onClick={() => {
                if (order.on_hold && userRole !== 'admin') {
                  toast.error('Only admin can release a hold');
                  return;
                }
                api.handleToggleHold(order.id);
              }}
              title={order.on_hold ? (userRole === 'admin' ? 'Release hold' : 'Only admin can release') : 'Put order on hold'}
              data-testid="hold-toggle-btn"
            >
              Hold {order.on_hold ? 'ON' : 'OFF'}
            </Button>
          )}
          {(order.status === 'pending' || order.status === 'confirmed' || ((order.status === 'dispatched' || order.status === 'delivered' || order.status === 'partial_dispatched') && isStrictAdmin)) && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs px-2 py-1 h-7"
              style={{ fontSize: '11px' }}
              onClick={() => api.openEditDialog(order)}
              data-testid="edit-order-btn"
              title={(order.status === 'dispatched' || order.status === 'delivered' || order.status === 'partial_dispatched') ? 'Admin edit — use with caution' : 'Edit order'}
            >
              <Pencil className="w-3 h-3" />
            </Button>
          )}
          {(order.status === 'dispatched' || order.status === 'delivered') && (
            <Button size="sm" variant="outline" className="text-xs px-2 py-1 h-7" style={{ fontSize: '11px' }} onClick={() => api.setInvoiceOrderId(order.id)} data-testid="view-invoice-btn" title="Print/view invoice">
              <FileText className="w-3 h-3" />
            </Button>
          )}
          {(order.status === 'dispatched' || order.status === 'delivered') && (
            <Button
              size="sm"
              className="text-xs px-2 py-1 h-7"
              style={{ fontSize: '11px', background: '#25D366', color: 'white', borderColor: '#25D366' }}
              onClick={() => api.shareOnWhatsApp(order)}
              title="Share dispatch details on WhatsApp"
              data-testid="row-whatsapp-share-btn"
            >
              <MessageCircle className="w-3 h-3" />
            </Button>
          )}
          {userRole === 'admin' && order.status === 'delivered' && (order.dispatches || []).some(d => d.delivered_at) && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs px-2 py-1 h-7"
              style={{ fontSize: '11px', borderColor: '#C05C35', color: '#C05C35' }}
              onClick={() => api.revertUndeliver(order)}
              title="Revert to In Transit (undelivered) — stock will be added back to facilities"
              data-testid="revert-undelivered-btn"
            >
              <Undo2 className="w-3 h-3 mr-1" /> Undeliver
            </Button>
          )}
          {userRole === 'admin' && order.status !== 'dispatched' && order.status !== 'delivered' && (
            <Button size="sm" variant="destructive" className="text-xs px-2 py-1 h-7" style={{ fontSize: '11px' }} onClick={() => api.deleteOrder(order)} data-testid="delete-order-btn">
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
});


// Orders Tab Component
/**
 * OrdersTab — the main Order Management view.
 *
 * `restrictTo` (optional): when set to a status string like `'dispatched'`,
 * the tab acts as a locked / read-only-filter view:
 *   - Status filter is forced to that value and the dropdown is disabled.
 *   - "Clear All" filter button is hidden so the user can't escape the lock.
 *   - The Reports tab uses `restrictTo='dispatched'` to surface a dedicated
 *     Dispatch Details view.
 */
export const OrdersTab = ({ orders, products, dealers = [], transporters = [], crmOutstandingByDealerId = {}, freightByOrderId = {}, fetchData, patchOrder, removeOrder, getStatusBadge, drillFilter, clearDrillFilter, userRole, onNewOrder, restrictTo, pendingEditOrderId, clearPendingEditOrderId, onPendingEditDialogClosed, hideTabBody = false, justCreatedOrderIds }) => {
  const confirm = useConfirm();
  const { can, user: authUser } = useAuth();
  const isStrictAdmin = authUser?.role === 'admin';
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [dispatchDialog, setDispatchDialog] = useState(false);
  const [dispatchFacility, setDispatchFacility] = useState('E-302');
  const [dispatchRemaining, setDispatchRemaining] = useState(null); // { items: [{product_id, product_name, ordered, remaining}], dispatches: [...] }
  const [dispatchSplitQty, setDispatchSplitQty] = useState({}); // { product_id: qty }
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [driverMobile, setDriverMobile] = useState('');
  const [dispatchTransporterId, setDispatchTransporterId] = useState('');
  const selectedTransporter = transporters.find(t => t.id === dispatchTransporterId) || null;
  const [invoiceOrderId, setInvoiceOrderId] = useState(null);
  const [deleteConfirmOrder, setDeleteConfirmOrder] = useState(null); // order pending delete confirmation
  const [deleting, setDeleting] = useState(false);
  // CRM-style dealer detail popup (Outstanding Invoices + Dispatches), opened by clicking the dealer name
  const [crmDealerDialog, setCrmDealerDialog] = useState(null); // { dealer: {id,name,town,phone_number}, orders, outstanding_invoices, _loading }
  const [postDispatchNotify, setPostDispatchNotify] = useState(null); // { order } or null

  const shareOrderOnWhatsApp = (order) => {
    const raw = order?.dealer_phone || '';
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) { toast.error('Dealer mobile number not available'); return; }
    const waNumber = digits.startsWith('91') && digits.length >= 12 ? digits : (digits.length === 10 ? `91${digits}` : digits);

    const fmtDate = (d) => {
      if (!d) return '';
      const dt = new Date(String(d).includes('T') ? d : d + 'T00:00:00');
      return dt.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const discountPct = order.discount_percent ?? 10;
    const gross = Number(order.total_amount || 0);
    // Net = sum of per-item nets, using precedence
    // item.discount_percent -> product master discount -> order.discount_percent -> 10%.
    // Matches the Orders-table "Bill Amt" column and the backend invoice endpoint.
    const productDiscByPid = (products || []).reduce((m, p) => { m[p.id] = p.discount_percent; return m; }, {});
    let net = 0;
    (order.items || []).forEach(it => {
      const pct = it.discount_percent != null ? Number(it.discount_percent) : (productDiscByPid[it.product_id] ?? discountPct);
      net += Number(it.total || 0) * (1 - pct / 100);
    });
    const discountAmt = gross - net;
    const effectivePct = gross > 0 ? (discountAmt / gross) * 100 : discountPct;
    const invoiceNum = `INV-${String(order.id).slice(0, 8).toUpperCase()}`;

    const lines = [];
    lines.push('*Vikas Industries*');
    lines.push('Palsana, Rajasthan');
    lines.push('');
    lines.push(`Namaste ${order.dealer_name || ''},`);
    lines.push('');
    lines.push(`Your order *${invoiceNum}* has been *${order.status === 'dispatched' ? 'dispatched' : order.status}*.`);
    lines.push('');
    if (order.delivery_date) {
      lines.push(`*Delivery:* ${fmtDate(order.delivery_date)}${order.delivery_time ? ' at ' + order.delivery_time : ''}`);
    }
    if (order.vehicle_number) lines.push(`*Vehicle:* ${order.vehicle_number}`);
    if (order.driver_mobile) lines.push(`*Driver:* ${order.driver_mobile}`);
    if (order.vehicle_number || order.delivery_date) lines.push('');

    lines.push('*Items:*');
    (order.items || []).forEach((it, i) => {
      lines.push(`${i + 1}. ${it.product_name} \u2014 ${it.quantity} x \u20B9${it.price} = \u20B9${it.total}`);
    });
    lines.push('');
    if (discountAmt > 0) {
      lines.push(`Gross: \u20B9${gross.toFixed(2)}`);
      lines.push(`Discount (${effectivePct.toFixed(2)}%): -\u20B9${discountAmt.toFixed(2)}`);
    }
    lines.push(`*Net Payable: \u20B9${net.toFixed(2)}*`);
    lines.push('');
    lines.push('Thank you for your business!');
    lines.push('\u2014 Vikas Industries');

    const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(lines.join('\n'))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Default to no schedule date filter when entering Orders from the main menu.
  // Dashboard tiles override these via drillFilter.sched_from / sched_to (see useEffect below).
  // When mounted in a locked view (e.g. Reports → Dispatch Details), default the
  // schedule-date range to TODAY so dispatchers immediately see today's dispatches.
  const todayISO = new Date().toISOString().split('T')[0];
  const [schedFrom, setSchedFrom] = useState(restrictTo ? todayISO : '');
  const [schedTo, setSchedTo] = useState(restrictTo ? todayISO : '');
  const [statusFilter, setStatusFilter] = useState(restrictTo || 'all');
  const statusFilterPrev = useRef(restrictTo || 'all');
  const [dealerQuery, setDealerQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [sortColumn, setSortColumn] = useState('order_date');
  const [reviewOrder, setReviewOrder] = useState(null);
  const [outstandingDialog, setOutstandingDialog] = useState(null); // { order, loading, detail, error }
  const [freightDialog, setFreightDialog] = useState(null); // { order, freight }
  const [editDialog, setEditDialog] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editDiscount, setEditDiscount] = useState(10);
  const [editDeliveryDate, setEditDeliveryDate] = useState('');
  const [editDeliveryTime, setEditDeliveryTime] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRescheduleDialog, setBulkRescheduleDialog] = useState(false);
  const [bulkConfirmDialog, setBulkConfirmDialog] = useState(false);
  const [bulkDeliveryDate, setBulkDeliveryDate] = useState('');
  const [bulkDeliveryTime, setBulkDeliveryTime] = useState('10:00');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [manualDrafts, setManualDrafts] = useState({}); // {orderId: string} local input buffer
  const [descDrafts, setDescDrafts] = useState({});
  // Windowing: render only the first `visibleCount` rows and lazily reveal more
  // as the user scrolls (IntersectionObserver sentinel) or clicks "Show more".
  // Keeps the initial paint + every re-render cheap even with hundreds of orders.
  const ROW_PAGE = 30;
  const [visibleCount, setVisibleCount] = useState(ROW_PAGE);

  // Stable handler bag for OrderRow. Identity NEVER changes (useMemo []), so
  // React.memo on OrderRow isn't defeated. Each method forwards to the latest
  // implementation captured in `rowFnsRef` (reassigned every render below),
  // so calls always see fresh state/closures — the classic "useEvent" pattern.
  const rowFnsRef = useRef({});
  const rowApi = useMemo(() => ({
    toggleSelectOne: (id) => rowFnsRef.current.toggleSelectOne(id),
    setManualDraft: (id, v) => rowFnsRef.current.setManualDrafts((prev) => ({ ...prev, [id]: v })),
    saveManualAmount: (id, v) => rowFnsRef.current.saveManualAmount(id, v),
    openCrm: (o) => rowFnsRef.current.openCrmDialogForOrder(o),
    setReviewOrder: (o) => rowFnsRef.current.setReviewOrder(o),
    setDescDraft: (id, v) => rowFnsRef.current.setDescDrafts((prev) => ({ ...prev, [id]: v })),
    saveManualDescription: (id, v) => rowFnsRef.current.saveManualDescription(id, v),
    openDispatch: (o) => rowFnsRef.current.openDispatchDialog(o),
    can: (s, a) => rowFnsRef.current.can(s, a),
    getRevertOptions: (st) => rowFnsRef.current.getRevertOptions(st),
    handleRevert: (o, v) => rowFnsRef.current.handleRevert(o, v),
    handleToggleHold: (id) => rowFnsRef.current.handleToggleHold(id),
    openEditDialog: (o) => rowFnsRef.current.openEditDialog(o),
    setInvoiceOrderId: (id) => rowFnsRef.current.setInvoiceOrderId(id),
    shareOnWhatsApp: (o) => rowFnsRef.current.shareOrderOnWhatsApp(o),
    revertUndeliver: (o) => rowFnsRef.current.revertOrderToUndelivered(o),
    deleteOrder: (o) => rowFnsRef.current.handleDeleteOrder(o),
    openOutstanding: (o) => rowFnsRef.current.openOutstandingDialog(o),
    openFreight: (o) => rowFnsRef.current.openFreightDialog(o),
    getStatusBadge: (st) => rowFnsRef.current.getStatusBadge(st),
    canDispatchOrder: (o) => rowFnsRef.current.canDispatchOrder(o),
    fmtDate: (d) => rowFnsRef.current.fmtDate(d),
    fmtDateOnly: (d) => rowFnsRef.current.fmtDateOnly(d),
    fmtTimeOnly: (d) => rowFnsRef.current.fmtTimeOnly(d),
  }), []);
  // Windowing sentinel + reset. Kept up here (before any early return) to obey
  // the rules-of-hooks. Reset to the first page whenever the filter/sort/search
  // inputs or the order set change, so the user starts at the top.
  const loadMoreRef = useRef(null);
  React.useEffect(() => {
    setVisibleCount(ROW_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerQuery, statusFilter, dateFrom, dateTo, schedFrom, schedTo, sortColumn, sortOrder, drillFilter?.type, drillFilter?.value, orders.length]);
  // Auto-reveal the next page as the sentinel scrolls into view. The sentinel
  // is only in the DOM while more rows remain, so this no-ops otherwise.
  React.useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setVisibleCount((c) => c + ROW_PAGE);
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [visibleCount]);

  // Auto-open review dialog when arriving via drill filter with auto_review flag
  React.useEffect(() => {
    if (drillFilter?.auto_review && orders.length > 0) {
      const match = orders.find(o => {
        if (drillFilter.type === 'dealer' && o.dealer_id !== drillFilter.value) return false;
        if (drillFilter.product_id && !o.items.some(i => i.product_id === drillFilter.product_id)) return false;
        return true;
      });
      if (match) setReviewOrder(match);
    }
  }, [drillFilter?.auto_review, drillFilter?.value, drillFilter?.product_id, orders]);

  // Cross-tab hand-off from PlanningTab "Edit Order" button:
  //   AdminDashboard sets `pendingEditOrderId` and switches to this tab.
  //   We pick it up here, find the order, open EditOrderDialog, then clear
  //   the trigger so revisiting the tab doesn't re-open it.
  //
  // We also ARM a one-shot "return-to-planning" flag so that when this very
  // edit dialog closes (cancel OR save) we can notify AdminDashboard to
  // navigate back to PlanningTab + restore the drill popup (user spec:
  // "always de-escalate one step only").
  const returnToPlanningArmedRef = React.useRef(false);
  React.useEffect(() => {
    if (!pendingEditOrderId || !orders.length) return;
    const match = orders.find(o => o.id === pendingEditOrderId);
    if (match) {
      returnToPlanningArmedRef.current = true;
      openEditDialog(match);
    }
    if (clearPendingEditOrderId) clearPendingEditOrderId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEditOrderId, orders]);

  // Fire the "return to planning" callback the moment the edit dialog closes
  // (TRANSITION true → false). One-shot — flag is disarmed immediately so a
  // subsequent locally-initiated edit doesn't bounce back to Planning.
  // We must compare against the PREVIOUS editDialog value (via ref) because
  // on initial mount this effect would otherwise see editDialog=false even
  // though the pendingEditOrderId effect (which runs in the same phase) just
  // armed the ref AND called setEditDialog(true) — that setState hasn't
  // committed yet, so the captured editDialog is still false and we would
  // erroneously fire the close callback before the dialog ever opens.
  const prevEditDialogRef = React.useRef(false);
  React.useEffect(() => {
    const wasOpen = prevEditDialogRef.current;
    prevEditDialogRef.current = editDialog;
    if (wasOpen && !editDialog && returnToPlanningArmedRef.current) {
      returnToPlanningArmedRef.current = false;
      if (typeof onPendingEditDialogClosed === 'function') {
        onPendingEditDialogClosed();
      }
    }
  }, [editDialog, onPendingEditDialogClosed]);

  // Drill-in from Dealers tab: clear schedule-date filters so all orders for that dealer show
  React.useEffect(() => {
    if (drillFilter?.clear_sched) {
      setSchedFrom('');
      setSchedTo('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillFilter?.clear_sched, drillFilter?.value]);

  // Drill-in from Dashboard tiles: pre-fill schedule/order date and status filters
  React.useEffect(() => {
    if (!drillFilter) return;
    if (drillFilter.sched_from !== undefined) setSchedFrom(drillFilter.sched_from);
    if (drillFilter.sched_to !== undefined) setSchedTo(drillFilter.sched_to);
    if (drillFilter.date_from !== undefined) setDateFrom(drillFilter.date_from);
    if (drillFilter.date_to !== undefined) setDateTo(drillFilter.date_to);
    if (drillFilter.status_filter !== undefined) setStatusFilter(drillFilter.status_filter);
    if (drillFilter.sort_column !== undefined) setSortColumn(drillFilter.sort_column);
    if (drillFilter.sort_order !== undefined) setSortOrder(drillFilter.sort_order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillFilter?.sched_from, drillFilter?.sched_to, drillFilter?.date_from, drillFilter?.date_to, drillFilter?.status_filter, drillFilter?.sort_column, drillFilter?.sort_order]);

  // When user switches Status filter to "Confirmed" / "Dispatched", apply a sensible default sort.
  // Skip on first mount; only react when statusFilter actually changes.
  React.useEffect(() => {
    if (statusFilterPrev.current !== statusFilter) {
      if (statusFilter === 'confirmed') {
        setSortColumn('seq');
        setSortOrder('oldest'); // 'oldest' = ascending direction in this table's convention
      } else if (statusFilter === 'dispatched') {
        setSortColumn('dispatch_date');
        setSortOrder('newest'); // descending — latest dispatch on top
      }
      statusFilterPrev.current = statusFilter;
    }
  }, [statusFilter]);

  const dealerTownById = React.useMemo(() => {
    const map = {};
    (dealers || []).forEach(d => { if (d.id) map[d.id] = d.town || ''; });
    return map;
  }, [dealers]);

  // Master-price map by product_id (used to flag rate overrides on order rows)
  const masterPriceByPid = React.useMemo(() => {
    const map = {};
    (products || []).forEach(p => { if (p && p.id) map[p.id] = Number(p.price); });
    return map;
  }, [products]);

  // Set of product IDs that are VIRTUAL (MISC-spawned / sub-brand variants).
  // Per user spec 18/Jun/2026: every order containing a virtual product is
  // treated as a rate-override regardless of whether its rate happens to
  // match the (often nominal) catalog price — virtuals don't have a
  // negotiated catalog rate, so the operator-entered rate IS the override.
  const virtualPidSet = React.useMemo(() => {
    const s = new Set();
    (products || []).forEach(p => { if (p && p.is_virtual) s.add(p.id); });
    return s;
  }, [products]);

  // Master discount % by product_id (per-product default)
  const productDiscountByPid = React.useMemo(() => {
    const map = {};
    (products || []).forEach(p => { if (p && p.id) map[p.id] = p.discount_percent == null ? 10 : Number(p.discount_percent); });
    return map;
  }, [products]);

  // Returns true if any item:
  //   (a) has a rate differing from the product master price (legacy check),
  //   OR
  //   (b) references a VIRTUAL product (no real catalog price — operator
  //       must always pick a rate, so it is by definition an override).
  // Ignores deleted products (no master entry).
  const isPriceOverridden = React.useCallback((order) => {
    for (const it of (order.items || [])) {
      if (it.product_id && virtualPidSet.has(it.product_id)) return true;
      const master = masterPriceByPid[it.product_id];
      if (master === undefined) continue;
      const paid = Number(it.price);
      if (Number.isFinite(paid) && Math.abs(paid - master) > 0.005) return true;
    }
    return false;
  }, [masterPriceByPid, virtualPidSet]);

  const saveManualAmount = async (orderId, raw) => {
    const parsed = raw === '' || raw === null || raw === undefined ? null : parseFloat(raw);
    if (raw !== '' && Number.isNaN(parsed)) return;
    try {
      const res = await orderAPI.updateManualAmount(orderId, { manual_amount: parsed });
      // Optimistic patch — reflect the saved manual_amount instantly without
      // waiting for the full /orders refetch. Background fetchData() still
      // reconciles derived aggregates. Fallback: mutate just the field if
      // the endpoint didn't return the full order object.
      if (typeof patchOrder === 'function') {
        if (res?.data?.id) patchOrder(res.data);
        else patchOrder({ id: orderId, manual_amount: parsed });
      }
      // Clear local buffer so persisted value shows from props
      setManualDrafts((prev) => {
        const n = { ...prev };
        delete n[orderId];
        return n;
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to save amount');
    }
  };

  const saveManualDescription = async (orderId, raw) => {
    try {
      const val = raw || '';
      const res = await orderAPI.updateManualDescription(orderId, { manual_description: val });
      if (typeof patchOrder === 'function') {
        if (res?.data?.id) patchOrder(res.data);
        else patchOrder({ id: orderId, manual_description: val });
      }
      setDescDrafts((prev) => {
        const n = { ...prev };
        delete n[orderId];
        return n;
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to save description');
    }
  };

  const handleConfirm = async () => {
    try {
      const res = await orderAPI.confirm(selectedOrder.id, { delivery_date: deliveryDate, delivery_time: deliveryTime });
      toast.success('Order confirmed');
      setConfirmDialog(false);
      setDeliveryDate('');
      setDeliveryTime('');
      if (res?.data && typeof patchOrder === 'function') patchOrder(res.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to confirm order');
    }
  };

  const openDispatchDialog = async (order) => {
    setSelectedOrder(order);
    setDispatchDialog(true);
    setDispatchRemaining(null);
    try {
      const res = await orderAPI.getRemaining(order.id);
      setDispatchRemaining(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load order details');
    }
  };

  const handleDispatch = async () => {
    if (!selectedOrder || !dispatchRemaining) return;
    const remainingItems = (dispatchRemaining.items || []).filter(i => i.remaining > 0);
    if (remainingItems.length === 0) { toast.error('Nothing remaining to dispatch'); return; }
    if (!vehicleNumber || !driverMobile) { toast.error('Vehicle & driver required'); return; }
    try {
      const res = await orderAPI.dispatchSplit(selectedOrder.id, {
        facility: null,        // facility is decided at delivery confirmation
        vehicle_number: vehicleNumber,
        driver_mobile: driverMobile,
        items: null,           // backend will default to all remaining items
      });
      const updated = res.data;
      const fully = updated.status === 'dispatched';
      toast.success(fully ? 'Order fully dispatched — confirm delivery from Stock > Delivery' : 'Split dispatched — confirm delivery from Stock > Delivery');
      const dispatchedSnapshot = { ...selectedOrder, status: updated.status, vehicle_number: vehicleNumber, driver_mobile: driverMobile };
      setDispatchDialog(false);
      setVehicleNumber('');
      setDriverMobile('');
      if (typeof patchOrder === 'function') patchOrder(updated);
      fetchData();
      setPostDispatchNotify({ order: dispatchedSnapshot });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dispatch order');
    }
  };

  const handleDeliver = async (orderId) => {
    try {
      const res = await orderAPI.deliver(orderId);
      toast.success('Order marked as delivered');
      if (res?.data && typeof patchOrder === 'function') patchOrder(res.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to mark order as delivered');
    }
  };

  const handlePayment = async () => {
    try {
      const amount = parseFloat(paymentAmount);
      const res = await orderAPI.updatePayment(selectedOrder.id, {
        payment_status: paymentStatus,
        amount_paid: amount
      });
      toast.success('Payment updated');
      setPaymentDialog(false);
      setPaymentAmount('');
      if (res?.data && typeof patchOrder === 'function') patchOrder(res.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to update payment');
    }
  };

  const handleEditOrder = async () => {
    try {
      // Schedule Date is mandatory for editable orders (non-dispatched).
      // Dispatched/delivered/partial orders skip this since the schedule is locked anyway.
      const isDispatched = selectedOrder && ['dispatched', 'delivered', 'partial_dispatched'].includes(selectedOrder.status);
      if (!isDispatched && !editDeliveryDate) {
        toast.error('Schedule Date is required for every order');
        return;
      }
      // Strip UI-only fields, coerce discount_percent to number (or null for blank), and
      // normalize empty strings → null for optional MISC fields. Sending empty strings
      // for parent_product_id corrupted Product Summary grouping (showed under "unnamed"
      // stack) because the aggregation's $ifNull only treats true null as missing.
      const cleanItems = editItems.map(({ _default_disc, _is_misc, _weight_edit, _variant_edit, ...rest }) => {
        const dp = rest.discount_percent;
        const blank = (v) => v === '' || v == null;
        // Normalize empty quantity → 1 (matches the placeholder). The
        // EditOrderDialog allows quantity='' as an intermediate typing state;
        // we recover to 1 here so the backend always receives a positive int.
        const qty = (rest.quantity === '' || rest.quantity == null) ? 1 : Number(rest.quantity);
        return {
          ...rest,
          quantity: qty,
          total: qty * Number(rest.price || 0),
          discount_percent: dp === '' || dp == null ? null : Number(dp),
          sub_brand_name: blank(rest.sub_brand_name) ? null : rest.sub_brand_name,
          parent_product_id: blank(rest.parent_product_id) ? null : rest.parent_product_id,
          parent_product_name: blank(rest.parent_product_name) ? null : rest.parent_product_name,
          variant: blank(rest.variant) ? null : rest.variant,
          weight_kg: rest.weight_kg == null || rest.weight_kg === '' ? null : Number(rest.weight_kg),
        };
      });
      const payload = { items: cleanItems, discount_percent: editDiscount };
      if (editDeliveryDate && !isDispatched) payload.delivery_date = editDeliveryDate;
      if (editDeliveryTime && !isDispatched) payload.delivery_time = editDeliveryTime;
      const res = await orderAPI.edit(selectedOrder.id, payload);
      toast.success('Order updated');
      setEditDialog(false);
      setEditItems([]);
      setEditDeliveryDate('');
      setEditDeliveryTime('');
      // Optimistic patch — instant UI reflect (skips the full /orders refetch lag).
      if (res?.data && typeof patchOrder === 'function') patchOrder(res.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to edit order');
    }
  };

  const openOutstandingDialog = async (order) => {
    setOutstandingDialog({ order, loading: true, detail: null, error: null });
    try {
      const res = await crmAPI.getDealerDetail(order.dealer_id);
      setOutstandingDialog({ order, loading: false, detail: res.data, error: null });
    } catch (e) {
      setOutstandingDialog({ order, loading: false, detail: null, error: e.response?.data?.detail || 'Failed to load outstanding details' });
    }
  };

  const openFreightDialog = (order) => {
    setFreightDialog({ order, freight: freightByOrderId[order.id] || { lines: [], by_dealer_total: 0, by_us_total: 0, total_freight: 0 } });
  };


  const openEditDialog = (order) => {
    setSelectedOrder(order);
    setEditItems(order.items.map(i => {
      const prod = (products || []).find(p => p.id === i.product_id);
      const isMisc = prod && prod.name === 'MISC';
      return {
        ...i,
        _default_disc: productDiscountByPid[i.product_id] ?? 10,
        discount_percent: i.discount_percent == null ? (productDiscountByPid[i.product_id] ?? 10) : i.discount_percent,
        _is_misc: !!isMisc,
        sub_brand_name: i.sub_brand_name || (isMisc ? (i.product_name || '') : ''),
        parent_product_id: i.parent_product_id || '',
        parent_product_name: i.parent_product_name || '',
        weight_kg: i.weight_kg ?? (isMisc ? 50 : null),
      };
    }));
    setEditDiscount(order.discount_percent ?? 10);
    setEditDeliveryDate(order.delivery_date || '');
    setEditDeliveryTime(order.delivery_time || '');
    setEditDialog(true);
  };

  const toggleSelectOne = (orderId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkReschedule = async () => {
    if (!bulkDeliveryDate || !bulkDeliveryTime) {
      toast.error('Please provide delivery date and time');
      return;
    }
    setBulkSubmitting(true);
    try {
      const res = await orderAPI.bulkReschedule({
        order_ids: Array.from(selectedIds),
        delivery_date: bulkDeliveryDate,
        delivery_time: bulkDeliveryTime
      });
      const updated = res.data.updated || 0;
      const skipped = (res.data.skipped || []).length;
      toast.success(`Rescheduled ${updated} order${updated !== 1 ? 's' : ''}${skipped ? ` (${skipped} skipped — dispatched/delivered orders cannot be rescheduled)` : ''}`);
      setBulkRescheduleDialog(false);
      clearSelection();
      setBulkDeliveryDate('');
      setBulkDeliveryTime('10:00');
      // Patch only the eligible rows in place (no full /orders refetch) so the
      // memoized rows update instantly without re-rendering the whole list.
      const now = new Date().toISOString();
      (res.data.eligible_ids || []).forEach((id) => {
        const o = orders.find((x) => x.id === id);
        if (o && typeof patchOrder === 'function') {
          patchOrder({ ...o, delivery_date: bulkDeliveryDate, delivery_time: bulkDeliveryTime, updated_at: now });
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk reschedule');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkConfirm = async () => {
    if (!bulkDeliveryDate || !bulkDeliveryTime) {
      toast.error('Please provide delivery date and time');
      return;
    }
    setBulkSubmitting(true);
    try {
      const res = await orderAPI.bulkConfirm({
        order_ids: Array.from(selectedIds),
        delivery_date: bulkDeliveryDate,
        delivery_time: bulkDeliveryTime
      });
      const updated = res.data.updated || 0;
      const skipped = (res.data.skipped || []).length;
      toast.success(`Confirmed ${updated} order${updated !== 1 ? 's' : ''}${skipped ? ` (${skipped} skipped — only pending can be confirmed)` : ''}`);
      setBulkConfirmDialog(false);
      clearSelection();
      setBulkDeliveryDate('');
      setBulkDeliveryTime('10:00');
      // Patch only the eligible rows in place (no full /orders refetch).
      const now = new Date().toISOString();
      (res.data.eligible_ids || []).forEach((id) => {
        const o = orders.find((x) => x.id === id);
        if (o && typeof patchOrder === 'function') {
          patchOrder({ ...o, status: 'confirmed', delivery_date: bulkDeliveryDate, delivery_time: bulkDeliveryTime, updated_at: now });
        }
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk confirm');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleToggleHold = async (orderId) => {
    try {
      const res = await orderAPI.toggleHold(orderId);
      // Optimistic UI — patch the single order locally so the row flips
      // instantly (no full /orders refetch lag). The backend response IS the
      // fresh authoritative doc; we intentionally SKIP a follow-up global
      // fetchData() here because it can race and flash-revert (bug reported
      // 04/Aug/2026). Any aggregate counters catch up on the next natural
      // refetch (edit / dispatch / tab remount).
      if (res?.data && typeof patchOrder === 'function') patchOrder(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle hold');
    }
  };

  const updateEditItemQty = (idx, delta) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i === idx) {
        // If quantity is currently blank (just-added line waiting for typing),
        // treat the base as 1 instead of NaN so '+' lands on 2 and '-' lands on 1.
        const base = (item.quantity === '' || item.quantity == null) ? 1 : (Number(item.quantity) || 0);
        const newQty = Math.max(1, base + delta);
        return { ...item, quantity: newQty, total: newQty * item.price };
      }
      return item;
    }));
  };

  const setEditItemQty = (idx, qty) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i === idx) {
        // Allow empty intermediate state ('') so the operator can clear the
        // field and type a fresh value. Commit-to-1 is handled onBlur in
        // EditOrderDialog. We DON'T multiply price by '' to avoid NaN totals,
        // so total falls back to 0 while the field is being typed.
        if (qty === '' || qty == null) {
          return { ...item, quantity: '', total: 0 };
        }
        const newQty = Math.max(1, parseInt(qty, 10) || 1);
        return { ...item, quantity: newQty, total: newQty * item.price };
      }
      return item;
    }));
  };

  const setEditItemPrice = (idx, price) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i === idx) {
        // Allow empty intermediate state; clamp to non-negative on commit
        const n = price === '' ? '' : Math.max(0, parseFloat(price));
        const newPrice = Number.isNaN(n) || n === '' ? item.price : n;
        return { ...item, price: newPrice, total: item.quantity * newPrice };
      }
      return item;
    }));
  };

  const setEditItemDiscount = (idx, pct) => {
    setEditItems(prev => prev.map((item, i) => {
      if (i === idx) {
        if (pct === '' || pct == null) return { ...item, discount_percent: '' };
        const n = parseFloat(pct);
        if (Number.isNaN(n)) return item;
        return { ...item, discount_percent: Math.min(100, Math.max(0, n)) };
      }
      return item;
    }));
  };

  const removeEditItem = (idx) => {
    if (editItems.length <= 1) return;
    setEditItems(editItems.filter((_, i) => i !== idx));
  };

  const miscProductId = React.useMemo(() => {
    const m = (products || []).find(p => p && p.name === 'MISC');
    return m ? m.id : null;
  }, [products]);

  // Sub-brands master list — loaded for Edit Order MISC dropdowns
  const [subBrands, setSubBrands] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    import('../../api').then(({ subBrandAPI }) => {
      subBrandAPI.getAll().then(r => { if (!cancelled) setSubBrands(r.data || []); }).catch(() => {});
    });
    return () => { cancelled = true; };
  }, []);

  const addEditItem = (productId) => {
    const p = products.find(pr => pr.id === productId);
    if (!p) return;
    const isMisc = p.name === 'MISC';
    // Always append a new line — allows multiple entries of the same product
    // (e.g. different rates / separate deliveries). Quantity starts BLANK so
    // the operator's first keystroke types the bag count directly into an
    // empty field. onBlur in EditOrderDialog restores quantity = 1 if left
    // empty. Much more reliable on mobile than fighting `.select()` on
    // <input type="number">.
    setEditItems([...editItems, {
      product_id: p.id,
      product_name: isMisc ? '' : p.name,
      quantity: '',
      price: isMisc ? 0 : p.price,
      total: 0,
      discount_percent: p.discount_percent == null ? 10 : p.discount_percent,
      _default_disc: p.discount_percent == null ? 10 : p.discount_percent,
      _is_misc: isMisc,
      sub_brand_name: '',
      parent_product_id: '',
      parent_product_name: '',
      weight_kg: isMisc ? 50 : null,
      variant: '',
    }]);
  };

  // Recompute MISC line-item display name from sub_brand + parent + weight + variant.
  // Format: "{sub_brand} {parent_name}[ {weight}kg][ {variant}]" — weight only when ≠ 50kg default; variant when set.
  const buildMiscName = (it) => {
    const sub = it.sub_brand_name || 'MISC';
    const parent = it.parent_product_name || '';
    const w = Number(it.weight_kg) || 50;
    const weightSuffix = w !== 50 ? ` ${w}kg` : '';
    const variantSuffix = it.variant ? ` ${it.variant}` : '';
    return `${sub}${parent ? ' ' + parent : ''}${weightSuffix}${variantSuffix}`.trim();
  };

  // Look up the historical MISC combo this dealer has paired with the given
  // sub-brand. Strictly dealer-scoped — if the dealer has no past order
  // using that sub-brand we return null and the caller leaves the other
  // fields blank (no cross-dealer fallback, per operator request).
  const findMiscPrefillCombo = (subBrand, dealerId) => {
    if (!subBrand || !dealerId) return null;
    const sorted = [...(orders || [])]
      .filter((o) => o.dealer_id === dealerId)
      .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    for (const o of sorted) {
      for (const it of (o.items || [])) {
        if ((it.sub_brand_name || '') !== subBrand) continue;
        return {
          sub_brand_name: it.sub_brand_name || '',
          parent_product_id: it.parent_product_id || '',
          parent_product_name: it.parent_product_name || '',
          weight_kg: Number(it.weight_kg) || 50,
          variant: it.variant || '',
          price: Number(it.price) || 0,
        };
      }
    }
    return null;
  };

  const setEditItemSubBrand = (idx, name) => {
    setEditItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, sub_brand_name: name };
      // ALWAYS overwrite the other MISC fields (parent / weight / variant /
      // rate) with the historical combo for the new sub-brand — but ONLY
      // when there's dealer-specific history. Operator-only edits to those
      // fields stick afterwards; changing the sub-brand again re-triggers
      // the fill. Parent selection is a plain edit (no vice-versa).
      const combo = findMiscPrefillCombo(name, selectedOrder?.dealer_id);
      let filled = 0;
      if (combo) {
        if (combo.parent_product_id && next.parent_product_id !== combo.parent_product_id) {
          next.parent_product_id = combo.parent_product_id;
          next.parent_product_name = combo.parent_product_name;
          filled++;
        }
        if (combo.weight_kg && Number(next.weight_kg) !== Number(combo.weight_kg)) {
          next.weight_kg = combo.weight_kg; filled++;
        }
        if ((combo.variant || '') !== (next.variant || '')) {
          next.variant = combo.variant || ''; filled++;
        }
        if (combo.price > 0 && Number(next.price || 0) !== Number(combo.price)) {
          next.price = combo.price;
          next.total = Number(next.price) * Number(next.quantity || 0);
          filled++;
        }
      }
      next.product_name = buildMiscName(next);
      if (filled > 0) setTimeout(() => toast.success(`Auto-filled ${filled} MISC field${filled === 1 ? '' : 's'} from this dealer's past preferences`), 0);
      return next;
    }));
  };

  const setEditItemParent = (idx, parentId) => {
    setEditItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const parent = (products || []).find(p => p.id === parentId);
      const next = { ...it, parent_product_id: parentId, parent_product_name: parent ? parent.name : '' };
      next.product_name = buildMiscName(next);
      return next;
    }));
  };

  const setEditItemWeight = (idx, weight) => {
    setEditItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, weight_kg: weight };
      next.product_name = buildMiscName(next);
      return next;
    }));
  };

  const setEditItemVariant = (idx, variant) => {
    setEditItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, variant };
      next.product_name = buildMiscName(next);
      return next;
    }));
  };

  // Toggle a UI-only flag on an edit item (e.g. _weight_edit / _variant_edit).
  // These flags drive whether the custom-input is visible without clobbering the
  // actual stored value, and are stripped before PUT in handleEditOrder via
  // destructuring of `_default_disc` / `_is_misc` / etc.
  const setEditItemFlag = (idx, key, value) => {
    setEditItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: value } : it));
  };

  const handleRevert = async (order, targetStatus) => {
    try {
      await orderAPI.revert(order.id, { target_status: targetStatus });
      toast.success(`Order reverted to ${targetStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert order');
    }
  };

  const getRevertOptions = (status) => {
    const map = {
      confirmed: ['pending'],
      dispatched: ['confirmed', 'pending']
    };
    let opts = map[status] || [];
    // Managers cannot revert any order to pending
    if (userRole === 'manager') {
      opts = opts.filter(s => s !== 'pending');
    }
    return opts;
  };

  const openCrmDialogForOrder = async (order) => {
    const base = {
      dealer: { id: order.dealer_id, name: order.dealer_name, town: dealerTownById[order.dealer_id] || '', phone_number: order.dealer_phone || '' },
      orders: [],
      outstanding_invoices: [],
      _loading: true,
    };
    setCrmDealerDialog(base);
    try {
      const res = await crmAPI.getDealerDetail(order.dealer_id);
      setCrmDealerDialog({ ...res.data, _loading: false });
    } catch (e) {
      setCrmDealerDialog({ ...base, _loading: false, _error: e.response?.data?.detail || 'Failed to load dealer details' });
    }
  };

  const handleDeleteOrder = (order) => {
    // Open the AlertDialog (replaces unreliable window.confirm which is blocked on many mobile browsers)
    setDeleteConfirmOrder(order);
  };

  const confirmDeleteOrder = async () => {
    if (!deleteConfirmOrder) return;
    setDeleting(true);
    try {
      const orderId = deleteConfirmOrder.id;
      await orderAPI.delete(orderId);
      toast.success('Order deleted');
      setDeleteConfirmOrder(null);
      // Optimistic remove — drops the row instantly without waiting for refetch.
      if (typeof removeOrder === 'function') removeOrder(orderId);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  const revertOrderToUndelivered = async (order) => {
    const delivered = (order.dispatches || []).filter(d => d.delivered_at);
    if (delivered.length === 0) return;
    const desc = delivered.length === 1
      ? 'Stock will be added back to the source facilities.'
      : `All ${delivered.length} delivered dispatches will go back to In Transit. Stock will be added back to the source facilities.`;
    if (!(await confirm({
      title: 'Revert delivery back to In Transit?',
      description: desc,
      confirmLabel: 'Revert',
      destructive: true,
    }))) return;
    try {
      // Revert each delivered dispatch sequentially (preserves audit trail per dispatch)
      for (const d of delivered) {
        await orderAPI.revertDelivery(order.id, d.id);
      }
      toast.success(delivered.length === 1 ? 'Delivery reverted — back to In Transit' : `${delivered.length} deliveries reverted — back to In Transit`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert delivery');
    }
  };

  if (invoiceOrderId) {
    return <InvoiceView orderId={invoiceOrderId} onBack={() => setInvoiceOrderId(null)} />;
  }

  // Apply drill filter from other sections
  const drillLabel = drillFilter
    ? drillFilter.type === 'dealer' ? `Dealer: ${drillFilter.label || drillFilter.value}`
    : drillFilter.type === 'status' ? `Status: ${drillFilter.value}`
    : drillFilter.type === 'order' ? `Order: #${drillFilter.value.slice(0, 8)}`
    : drillFilter.type === 'product' ? `Product: ${drillFilter.label || drillFilter.value}`
    : null : null;

  // Filter orders
  const filteredOrders = orders.filter((order) => {
    // Map `delivered` and `partial_dispatched` into the same "dispatched"
    // bucket so all three appear together under the Dispatched filter and
    // under the Dashboard Dispatched-tile drill-in. Used by both the
    // drillFilter status check below and the explicit Status dropdown
    // filter further down.
    const DISPATCHED_BUCKET = (s) => s === 'dispatched' || s === 'partial_dispatched' || s === 'delivered';
    const effectiveStatus = DISPATCHED_BUCKET(order.status) ? 'dispatched' : order.status;

    // Drill filter takes priority
    if (drillFilter) {
      if (drillFilter.type === 'dealer' && order.dealer_id !== drillFilter.value) return false;
      if (drillFilter.type === 'status' && drillFilter.value !== 'all') {
        // Compare against `effectiveStatus` so a drill-in of value="dispatched"
        // still matches `delivered` + `partial_dispatched` rows. Without this,
        // the UI silently dropped delivered orders that the PDF (server-side)
        // and the Dashboard tile both counted.
        if (effectiveStatus !== drillFilter.value && order.status !== drillFilter.value) return false;
      }
      if (drillFilter.type === 'order' && order.id !== drillFilter.value) return false;
      if (drillFilter.type === 'product' && !order.items.some(i => i.product_id === drillFilter.value)) return false;
      // Optional additional product filter for composite drill-downs (e.g. from Product Summary)
      if (drillFilter.product_id && !order.items.some(i => i.product_id === drillFilter.product_id)) return false;
    }
    // Order date range
    const orderDate = order.created_at?.slice(0, 10) || '';
    if (dateFrom && orderDate < dateFrom) return false;
    if (dateTo && orderDate > dateTo) return false;
    // Schedule date range
    const schedDate = order.delivery_date || '';
    if (schedFrom && (!schedDate || schedDate < schedFrom)) return false;
    if (schedTo && (!schedDate || schedDate > schedTo)) return false;
    // Status filter — same DISPATCHED_BUCKET applies. `all` excludes the
    // bucket unless caller opts in via `drillFilter.include_dispatched`.
    if (statusFilter === 'all' && effectiveStatus === 'dispatched' && !drillFilter?.include_dispatched) return false;
    if (statusFilter !== 'all' && effectiveStatus !== statusFilter) return false;
    // Dealer name / location text search — tokenized AND-match across name + phone + town.
    // Typing "ravi palsana" matches a dealer named "Ravi Kumar" in town "Palsana" (and
    // "palsana ravi" works too — order doesn't matter). Single token still matches any
    // field. Empty tokens (from extra whitespace) are ignored.
    if (dealerQuery) {
      const tokens = dealerQuery.toLowerCase().split(/\s+/).filter(Boolean);
      if (tokens.length) {
        const hay = `${order.dealer_name || ''} ${order.dealer_phone || ''} ${dealerTownById[order.dealer_id] || ''}`.toLowerCase();
        if (!tokens.every(t => hay.includes(t))) return false;
      }
    }
    return true;
  });

  // Today's date in local YYYY-MM-DD format — used to gate Dispatch button (only orders
  // scheduled for today can be dispatched, preventing premature stock moves).
  const todayStr = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();
  const canDispatchOrder = (order) => (order.delivery_date || '').slice(0, 10) === todayStr;

  // Group by date
  const groupedOrders = {};
  filteredOrders.forEach((order) => {
    const dateKey = order.created_at?.slice(0, 10) || 'Unknown';
    if (!groupedOrders[dateKey]) groupedOrders[dateKey] = [];
    groupedOrders[dateKey].push(order);
  });
  const sortedDates = Object.keys(groupedOrders).sort((a, b) => sortOrder === 'newest' ? b.localeCompare(a) : a.localeCompare(b));

  const clearFilters = () => { setDateFrom(''); setDateTo(''); setSchedFrom(''); setSchedTo(''); setStatusFilter(restrictTo || 'all'); };

  const hasFilters = dateFrom || dateTo || schedFrom || schedTo || statusFilter !== 'all';

  const handleExport = (type) => {
    const token = localStorage.getItem('token');
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (schedFrom) params.sched_from = schedFrom;
    if (schedTo) params.sched_to = schedTo;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (sortColumn) params.sort_by = sortColumn;
    if (sortOrder) params.sort_order = sortOrder;
    // Mirror on-screen text search & drill filter so the PDF matches exactly
    if (dealerQuery) params.q = dealerQuery;
    if (drillFilter) {
      if (drillFilter.type === 'dealer' && drillFilter.value) params.dealer_id = drillFilter.value;
      if (drillFilter.type === 'product' && drillFilter.value) params.product_id = drillFilter.value;
      if (drillFilter.product_id) params.product_id = drillFilter.product_id;
      if (drillFilter.type === 'status' && drillFilter.value && drillFilter.value !== 'all') params.status = drillFilter.value;
    }
    const url = type === 'excel' ? exportAPI.ordersExcelUrl(params) : exportAPI.ordersPDFUrl(params);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `orders_report.${type === 'excel' ? 'xlsx' : 'pdf'}`;
        link.click();
        window.URL.revokeObjectURL(link.href);
        toast.success(`${type === 'excel' ? 'Excel' : 'PDF'} downloaded`);
      })
      .catch(() => toast.error(`Failed to download ${type}`));
  };

  // Helper to get product description string
  const getProductDesc = (items) => items.map(i => i.product_name).join(', ');
  const getTotalQty = (items) => items.reduce((s, i) => s + i.quantity, 0);
  const fmtDate = (d) => d ? new Date(d.includes('T') ? d : d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
  const fmtDateTime = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const fmtDateOnly = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';
  const fmtTimeOnly = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';

  // Hand-off from PlanningTab "Edit Order": render ONLY the edit dialog over
  // a dim background (no orders list visible behind). Closing the dialog
  // fires `onPendingEditDialogClosed` → AdminDashboard switches back to
  // PlanningTab and restores the drill popup (user spec: "de-escalate one
  // step only"). Placed AFTER the helpers above so they're available in scope.
  if (hideTabBody) {
    return (
      <>
        <div className="min-h-[60vh] flex items-center justify-center" data-testid="orders-tab-hidden-for-planning-edit">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#5C6B61' }} />
        </div>
        <EditOrderDialog
          open={editDialog}
          onOpenChange={setEditDialog}
          selectedOrder={selectedOrder}
          editItems={editItems}
          products={products}
          subBrands={subBrands}
          editDeliveryDate={editDeliveryDate}
          setEditDeliveryDate={setEditDeliveryDate}
          editDeliveryTime={editDeliveryTime}
          setEditDeliveryTime={setEditDeliveryTime}
          productDiscountByPid={productDiscountByPid}
          fmtDate={fmtDate}
          setEditItemSubBrand={setEditItemSubBrand}
          setEditItemParent={setEditItemParent}
          setEditItemFlag={setEditItemFlag}
          setEditItemWeight={setEditItemWeight}
          setEditItemVariant={setEditItemVariant}
          updateEditItemQty={updateEditItemQty}
          setEditItemQty={setEditItemQty}
          setEditItemDiscount={setEditItemDiscount}
          setEditItemPrice={setEditItemPrice}
          removeEditItem={removeEditItem}
          addEditItem={addEditItem}
          onSave={handleEditOrder}
          isStrictAdmin={isStrictAdmin}
        />
      </>
    );
  }

  const getSortValue = (order) => {
    if (sortColumn === 'order_date') return order.created_at || '';
    if (sortColumn === 'schedule_date') {
      // Dispatched/delivered rows display the actual dispatch date+time;
      // sort by the latest dispatched_at (fallback: updated_at). Non-dispatched
      // rows fall back to scheduled delivery date + time.
      if (order.status === 'dispatched' || order.status === 'delivered') {
        const latest = (order.dispatches || []).reduce((m, d) => {
          const t = d?.dispatched_at || '';
          return t > m ? t : m;
        }, '');
        return latest || order.updated_at || '';
      }
      return (order.delivery_date || '') + (order.delivery_time || '');
    }
    if (sortColumn === 'dispatch_date') {
      if (!order.vehicle_number) return '';
      const latest = (order.dispatches || []).reduce((m, d) => {
        const t = d?.dispatched_at || '';
        return t > m ? t : m;
      }, '');
      return latest || order.updated_at || '';
    }
    if (sortColumn === 'dealer_name') return (order.dealer_name || '').toUpperCase();
    return order.created_at || '';
  };

  const toggleSort = (col) => {
    if (sortColumn === col) {
      setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest');
    } else {
      setSortColumn(col);
      // For dealer_name, default to A→Z (which is 'oldest' in existing convention because it maps to a.localeCompare(b))
      setSortOrder(col === 'dealer_name' ? 'oldest' : 'newest');
    }
  };

  const sortedFilteredOrders = [...filteredOrders].sort((a, b) => {
    if (sortColumn === 'seq') {
      const hasA = a.manual_amount !== null && a.manual_amount !== undefined;
      const hasB = b.manual_amount !== null && b.manual_amount !== undefined;
      // Push empty Seq values to the bottom regardless of direction
      if (!hasA && !hasB) return 0;
      if (!hasA) return 1;
      if (!hasB) return -1;
      const numA = Number(a.manual_amount);
      const numB = Number(b.manual_amount);
      return sortOrder === 'newest' ? numB - numA : numA - numB;
    }
    const valA = getSortValue(a);
    const valB = getSortValue(b);
    return sortOrder === 'newest' ? valB.localeCompare(valA) : valA.localeCompare(valB);
  });

  // Only render the first `visibleCount` rows (windowing). Reset + observer
  // hooks live near the top of the component (before the early returns) to
  // satisfy the rules-of-hooks; here we only derive plain values.
  const totalRows = sortedFilteredOrders.length;
  const windowedOrders = sortedFilteredOrders.slice(0, visibleCount);
  const hasMoreRows = totalRows > visibleCount;

  // Reassign the latest handler implementations every render so the stable
  // `rowApi` forwarders (above) always call fresh closures. Placed after all
  // handlers + helpers are defined; skipped harmlessly on the early returns
  // above (rows aren't rendered there anyway).
  rowFnsRef.current = {
    toggleSelectOne, setManualDrafts, saveManualAmount, openCrmDialogForOrder,
    setReviewOrder, setDescDrafts, saveManualDescription, openDispatchDialog,
    can, getRevertOptions, handleRevert, handleToggleHold, openEditDialog,
    setInvoiceOrderId, shareOrderOnWhatsApp, revertOrderToUndelivered,
    handleDeleteOrder, openOutstandingDialog, openFreightDialog, getStatusBadge, canDispatchOrder,
    fmtDate, fmtDateOnly, fmtTimeOnly,
  };

  return (
    <div data-testid="admin-orders-section">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 md:mb-6">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#2B5339' }}>Orders Management</h2>
        {onNewOrder && !restrictTo && (
          <Button onClick={onNewOrder} style={{ background: '#2B5339' }} data-testid="orders-new-order-btn">
            <Plus className="w-4 h-4 mr-2" /> New Order
          </Button>
        )}
      </div>

      {/* Dealer / Location search */}
      <div className="mb-4">
        <div className="relative">
          <Input
            type="search"
            placeholder="Search by dealer name, location or mobile..."
            value={dealerQuery}
            onChange={(e) => setDealerQuery(e.target.value)}
            className="w-full md:max-w-md pr-8"
            data-testid="orders-dealer-search"
          />
          {dealerQuery && (
            <button
              type="button"
              onClick={() => setDealerQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded hover:bg-[#FBF9F6]"
              style={{ color: '#5C6B61' }}
              data-testid="orders-dealer-search-clear"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Drill filter breadcrumb */}
      {drillFilter && drillLabel && (
        <div className="flex items-center gap-3 mb-4 p-3 rounded" style={{ background: '#2B5339' }} data-testid="drill-filter-banner">
          <p className="text-sm text-white flex-1">
            Showing orders for: <span className="font-bold">{drillLabel}</span>
          </p>
          <Button size="sm" variant="outline" className="text-xs bg-white" onClick={clearDrillFilter} data-testid="clear-drill-btn">
            Show All Orders
          </Button>
        </div>
      )}

      {/* Filters */}
      <Card className="p-5 bg-white border mb-6" style={{ borderColor: '#E2DFD8' }}>
        <div className="flex flex-wrap items-end gap-4 mb-3">
          {/* Row 1: Status + Sort */}
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => { if (!restrictTo) setStatusFilter(e.target.value); }}
              disabled={!!restrictTo}
              className="px-3 py-2 border rounded text-sm h-10 w-40"
              style={{ borderColor: '#E2DFD8', background: restrictTo ? '#F5F1E8' : 'white', cursor: restrictTo ? 'not-allowed' : 'pointer' }}
              data-testid="filter-status"
              title={restrictTo ? `Locked to ${restrictTo} on this tab` : 'Filter by order status'}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="dispatched">Dispatched</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Sort By</label>
            <div className="flex gap-1 h-10">
              <Button size="sm" className="text-xs h-10 px-3" style={sortColumn === 'order_date' ? { background: '#2B5339' } : { background: 'white', color: '#5C6B61', border: '1px solid #E2DFD8' }} onClick={() => toggleSort('order_date')} data-testid="topbar-sort-order-date">
                Order Date {sortColumn === 'order_date' ? (sortOrder === 'newest' ? '▼' : '▲') : ''}
              </Button>
              <Button size="sm" className="text-xs h-10 px-3" style={sortColumn === 'schedule_date' ? { background: '#2B5339' } : { background: 'white', color: '#5C6B61', border: '1px solid #E2DFD8' }} onClick={() => toggleSort('schedule_date')} data-testid="topbar-sort-schedule-date">
                Schedule Date {sortColumn === 'schedule_date' ? (sortOrder === 'newest' ? '▼' : '▲') : ''}
              </Button>
            </div>
          </div>
          {hasFilters && !restrictTo && (
            <Button variant="outline" size="sm" onClick={clearFilters} className="h-10" data-testid="clear-filters-btn">Clear All</Button>
          )}
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Export</label>
            <div className="flex gap-1 h-10">
              <Button size="sm" variant="outline" className="text-xs h-10 px-3" onClick={() => handleExport('excel')} data-testid="export-orders-excel">
                <Download className="w-3 h-3 mr-1" /> Excel
              </Button>
              <Button size="sm" className="text-xs h-10 px-3" style={{ background: '#C05C35' }} onClick={() => handleExport('pdf')} data-testid="export-orders-pdf">
                <Download className="w-3 h-3 mr-1" /> PDF
              </Button>
            </div>
          </div>
          <div className="ml-auto text-right">
            <p className="text-sm font-medium" style={{ color: '#2B5339' }} data-testid="filtered-count">{filteredOrders.length} orders</p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-4 pt-3" style={{ borderTop: '1px solid #E2DFD8' }}>
          {/* Row 2: Double date system */}
          <div className="flex items-end gap-2 p-2 rounded" style={{ background: '#FBF9F6' }}>
            <p className="text-xs font-bold pb-2" style={{ color: '#2B5339', minWidth: '70px' }}>Order Date</p>
            <div>
              <label className="text-[10px] font-medium block mb-1" style={{ color: '#5C6B61' }}>From</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-28 h-9 text-xs" data-testid="filter-date-from" />
            </div>
            <div>
              <label className="text-[10px] font-medium block mb-1" style={{ color: '#5C6B61' }}>To</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-28 h-9 text-xs" data-testid="filter-date-to" />
            </div>
          </div>
          <div className="flex items-end gap-2 p-2 rounded" style={{ background: '#FBF9F6' }}>
            <p className="text-xs font-bold pb-2" style={{ color: '#C05C35', minWidth: '70px' }}>Schedule Date</p>
            <div>
              <label className="text-[10px] font-medium block mb-1" style={{ color: '#5C6B61' }}>From</label>
              <Input type="date" value={schedFrom} onChange={(e) => setSchedFrom(e.target.value)} className="w-28 h-9 text-xs" data-testid="filter-sched-from" />
            </div>
            <div>
              <label className="text-[10px] font-medium block mb-1" style={{ color: '#5C6B61' }}>To</label>
              <Input type="date" value={schedTo} onChange={(e) => setSchedTo(e.target.value)} className="w-28 h-9 text-xs" data-testid="filter-sched-to" />
            </div>
          </div>
        </div>
      </Card>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 p-3 rounded sticky top-14 md:top-0 z-20" style={{ background: '#2B5339' }} data-testid="bulk-action-bar">
          <p className="text-sm text-white flex-1">
            <span className="font-bold" data-testid="bulk-selected-count">{selectedIds.size}</span> order{selectedIds.size !== 1 ? 's' : ''} selected
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="text-xs h-9 px-3" style={{ background: '#2B5339' }} onClick={() => { if (!bulkDeliveryDate) setBulkDeliveryDate(todayStr); setBulkConfirmDialog(true); }} data-testid="bulk-confirm-btn">
              <CheckCircle className="w-3 h-3 mr-1" /> Confirm Selected
            </Button>
            <Button size="sm" className="text-xs h-9 px-3" style={{ background: '#C05C35' }} onClick={() => setBulkRescheduleDialog(true)} data-testid="bulk-reschedule-btn">
              <CalendarClock className="w-3 h-3 mr-1" /> Reschedule Selected
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-9 px-3 bg-white" onClick={clearSelection} data-testid="bulk-clear-btn">
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      {sortedFilteredOrders.length === 0 ? (
        <Card className="p-12 text-center" style={{ borderColor: '#E2DFD8' }}>
          <p style={{ color: '#5C6B61' }}>No orders found for the selected filters</p>
        </Card>
      ) : (
        <Card className="bg-white border overflow-hidden" style={{ borderColor: '#E2DFD8' }}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] border-collapse">
              <thead>
                <tr style={{ background: '#FBF9F6' }}>
                  <th className="py-3 px-2 text-center" style={{ width: '32px' }}>
                    <input
                      type="checkbox"
                      checked={sortedFilteredOrders.length > 0 && sortedFilteredOrders.every(o => o.status === 'delivered' || o.status === 'dispatched' || o.status === 'partial_dispatched' || selectedIds.has(o.id))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const eligible = sortedFilteredOrders.filter(o => o.status !== 'delivered' && o.status !== 'dispatched' && o.status !== 'partial_dispatched').map(o => o.id);
                          setSelectedIds(new Set(eligible));
                        } else {
                          clearSelection();
                        }
                      }}
                      className="cursor-pointer"
                      data-testid="bulk-select-all"
                    />
                  </th>
                  <th className="text-center py-3 px-2 text-xs font-semibold cursor-pointer select-none" style={{ color: sortColumn === 'seq' ? '#2B5339' : '#5C6B61' }} onClick={() => toggleSort('seq')} data-testid="sort-seq">
                    S.No / Seq {sortColumn === 'seq' ? (sortOrder === 'newest' ? '▼' : '▲') : ''}
                  </th>
                  <th className="text-left py-3 px-2 text-xs font-semibold cursor-pointer select-none" style={{ color: sortColumn === 'dealer_name' ? '#2B5339' : '#5C6B61', minWidth: '220px' }} onClick={() => toggleSort('dealer_name')} title="Sort dealers alphabetically" data-testid="sort-dealer">
                    Party {sortColumn === 'dealer_name' ? (sortOrder === 'oldest' ? '↑' : '↓') : '⇅'}
                  </th>
                  <th className="text-left py-3 px-2 text-xs font-semibold" style={{ color: '#5C6B61', minWidth: '220px' }} data-testid="col-header-product-list">
                    Product / Qty
                  </th>
                  <th className="text-center py-3 px-2 text-xs font-semibold cursor-pointer select-none" style={{ color: sortColumn === 'schedule_date' ? '#2B5339' : '#5C6B61', minWidth: '140px' }} onClick={() => toggleSort('schedule_date')} data-testid="sort-schedule-date">
                    Sch / Disp {sortColumn === 'schedule_date' ? (sortOrder === 'newest' ? '▼' : '▲') : ''}
                  </th>
                  <th className="text-left py-3 px-2 text-xs font-semibold" style={{ color: '#5C6B61', minWidth: '220px' }} data-testid="col-header-product">
                    Description
                  </th>
                  <th className="text-left p-3 text-xs font-semibold" style={{ color: '#5C6B61' }} title="Bill amount on top, Outstanding (CRM) below in red">Bill Amt / Outstandings</th>
                  <th className="text-left p-3 text-xs font-semibold" style={{ color: '#5C6B61' }} title="Status on top, row actions below">Status / Actions</th>
                </tr>
              </thead>
              <tbody>
                {windowedOrders.map((order, idx) => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    idx={idx}
                    isSelected={selectedIds.has(order.id)}
                    isJustCreated={!!(justCreatedOrderIds && justCreatedOrderIds.has && justCreatedOrderIds.has(order.id))}
                    manualDraft={manualDrafts[order.id]}
                    descDraft={descDrafts[order.id]}
                    town={dealerTownById[order.dealer_id] || '-'}
                    outstanding={crmOutstandingByDealerId[order.dealer_id]}
                    freight={freightByOrderId[order.id]}
                    priceOverridden={isPriceOverridden(order)}
                    productDiscountByPid={productDiscountByPid}
                    userRole={userRole}
                    isStrictAdmin={isStrictAdmin}
                    api={rowApi}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {hasMoreRows && (
            <div ref={loadMoreRef} className="flex items-center justify-center py-4" style={{ borderTop: '1px solid #E2DFD8', background: '#FBF9F6' }} data-testid="orders-load-more">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setVisibleCount((c) => c + ROW_PAGE)}
                data-testid="orders-show-more-btn"
              >
                Show more ({totalRows - windowedOrders.length} more)
              </Button>
            </div>
          )}
        </Card>
      )}

      <ConfirmOrderDialog
        open={confirmDialog}
        onOpenChange={setConfirmDialog}
        selectedOrder={selectedOrder}
        deliveryDate={deliveryDate}
        setDeliveryDate={setDeliveryDate}
        deliveryTime={deliveryTime}
        setDeliveryTime={setDeliveryTime}
        onConfirm={handleConfirm}
      />

      <DispatchOrderDialog
        open={dispatchDialog}
        onOpenChange={(o) => { setDispatchDialog(o); if (!o) { setDispatchTransporterId(''); setVehicleNumber(''); setDriverMobile(''); setDispatchRemaining(null); } }}
        selectedOrder={selectedOrder}
        dispatchRemaining={dispatchRemaining}
        transporters={transporters}
        dispatchTransporterId={dispatchTransporterId}
        setDispatchTransporterId={setDispatchTransporterId}
        selectedTransporter={selectedTransporter}
        vehicleNumber={vehicleNumber}
        setVehicleNumber={setVehicleNumber}
        driverMobile={driverMobile}
        setDriverMobile={setDriverMobile}
        onDispatch={handleDispatch}
      />

      <DeleteOrderDialog
        order={deleteConfirmOrder}
        onCancel={() => setDeleteConfirmOrder(null)}
        onConfirm={confirmDeleteOrder}
        deleting={deleting}
      />

      <PostDispatchNotifyDialog
        notify={postDispatchNotify}
        onSkip={() => setPostDispatchNotify(null)}
        onShare={() => { shareOrderOnWhatsApp(postDispatchNotify.order); setPostDispatchNotify(null); }}
      />

      <ReviewOrderDialog
        reviewOrder={reviewOrder}
        onClose={() => setReviewOrder(null)}
        getStatusBadge={getStatusBadge}
        fmtDate={fmtDate}
        fmtDateTime={fmtDateTime}
        productDiscountByPid={productDiscountByPid}
        masterPriceByPid={masterPriceByPid}
        virtualPidSet={virtualPidSet}
        canDispatchOrder={canDispatchOrder}
        openDispatchDialog={openDispatchDialog}
        openEditDialog={openEditDialog}
        isStrictAdmin={isStrictAdmin}
      />

      {/* Outstanding Detail Dialog */}

      <CrmDealerDialog
        crmDealerDialog={crmDealerDialog}
        onClose={() => setCrmDealerDialog(null)}
        setReviewOrder={setReviewOrder}
        products={products}
      />


      <OutstandingDetailDialog
        outstandingDialog={outstandingDialog}
        onClose={() => setOutstandingDialog(null)}
        crmOutstandingByDealerId={crmOutstandingByDealerId}
      />

      <FreightDetailDialog
        freightDialog={freightDialog}
        onClose={() => setFreightDialog(null)}
      />


      <EditOrderDialog
        open={editDialog}
        onOpenChange={setEditDialog}
        selectedOrder={selectedOrder}
        editItems={editItems}
        products={products}
        subBrands={subBrands}
        editDeliveryDate={editDeliveryDate}
        setEditDeliveryDate={setEditDeliveryDate}
        editDeliveryTime={editDeliveryTime}
        setEditDeliveryTime={setEditDeliveryTime}
        productDiscountByPid={productDiscountByPid}
        fmtDate={fmtDate}
        setEditItemSubBrand={setEditItemSubBrand}
        setEditItemParent={setEditItemParent}
        setEditItemFlag={setEditItemFlag}
        setEditItemWeight={setEditItemWeight}
        setEditItemVariant={setEditItemVariant}
        updateEditItemQty={updateEditItemQty}
        setEditItemQty={setEditItemQty}
        setEditItemDiscount={setEditItemDiscount}
        setEditItemPrice={setEditItemPrice}
        removeEditItem={removeEditItem}
        addEditItem={addEditItem}
        onSave={handleEditOrder}
        isStrictAdmin={isStrictAdmin}
      />

      <BulkRescheduleDialog
        open={bulkRescheduleDialog}
        onOpenChange={setBulkRescheduleDialog}
        selectedCount={selectedIds.size}
        bulkDeliveryDate={bulkDeliveryDate}
        setBulkDeliveryDate={setBulkDeliveryDate}
        bulkDeliveryTime={bulkDeliveryTime}
        setBulkDeliveryTime={setBulkDeliveryTime}
        onSubmit={handleBulkReschedule}
        submitting={bulkSubmitting}
      />

      <BulkConfirmDialog
        open={bulkConfirmDialog}
        onOpenChange={setBulkConfirmDialog}
        selectedCount={selectedIds.size}
        bulkDeliveryDate={bulkDeliveryDate}
        setBulkDeliveryDate={setBulkDeliveryDate}
        bulkDeliveryTime={bulkDeliveryTime}
        setBulkDeliveryTime={setBulkDeliveryTime}
        onSubmit={handleBulkConfirm}
        submitting={bulkSubmitting}
      />
    </div>
  );
};
